import React from "react";
import { styled } from "@mui/material/styles";
import type { Theme } from "@mui/material/styles";
import { Container, Typography, Grid } from "@mui/material";

/**
 * PM5 T2 (money path, batch "freetrial-buynow") — converted from MUI v4 +
 * `makeStyles` to MUI v5+ `styled`/`sx`.
 * See docs/epics/pm5-mui-v5-migration.md §3 (T2) and docs/mui-conversion-guide.md.
 *
 * The "What is FarmFLiX?" feature block on /free-trial. Static marketing copy;
 * styling-only conversion, no DOM node or text changed.
 *
 * DEAD RULES DROPPED (guide §1.2 row 1): `button` and `secondaryButton` were
 * declared in the v4 `useStyles` object but never referenced from the JSX.
 */

// was: useStyles().background — a bare <div>, which has no `sx` prop.
const Background = styled("div")(({ theme }) => ({
  backgroundColor: theme.palette.background.ffNavy,
  padding: theme.spacing(6, 0),
  [theme.breakpoints.up("sm")]: {
    padding: theme.spacing(8, 0, 10, 0),
  },
}));

/**
 * ♦ v4's `<Grid container spacing={2}>` was implemented as
 *   `.MuiGrid-spacing-xs-2      { margin: -8px; width: calc(100% + 16px) }`
 *   `.MuiGrid-spacing-xs-2 > .MuiGrid-item { padding: 8px }`
 * v9 replaced that with `gap` on the container and NO padding on the items
 * (guide §2, "Grid `spacing` implementation" + its T2/signin refinement).
 * Rather than re-derive the flex-line arithmetic, v4's own declarations are
 * carried verbatim: `spacing` is omitted (v9 default 0 ⇒ `gap: 0px`), the
 * container restates the negative margin and the over-wide width, and every
 * item carries the 8px padding itself. Identical by construction.
 */
const v4SpacingContainerSx = {
  width: "calc(100% + 16px)",
  margin: "-8px",
};
const v4GridItemSx = { padding: "8px" };

// was: useStyles().title
const titleSx = (theme: Theme) => ({
  letterSpacing: "-2px",
  fontSize: "2rem",
  lineHeight: "2.2rem",
  fontWeight: 600,
  textShadow: "-2px 2px 2px #171717",
  "& span": {
    color: "background.ffGreen",
  },
  [theme.breakpoints.up("sm")]: {
    fontSize: "3rem",
    lineHeight: "3.2rem",
  },
});

// was: useStyles().titleLarge
const titleLargeSx = (theme: Theme) => ({
  letterSpacing: "-2px",
  fontSize: "2.8rem",
  lineHeight: "2.8rem",
  fontWeight: 600,
  textShadow: "-2px 2px 2px #171717",
  [theme.breakpoints.up("sm")]: {
    fontSize: "5.4rem",
    lineHeight: "5rem",
  },
});

// was: the `spacing={2}` item padding + useStyles().textContainer, merged.
// `Grid`'s `sx` is not `SxProps` and rejects the array form (guide §2,
// T2/onboarding), so the layers are merged into one object — v4's library
// padding first, the file's own rule after, matching v4's source order. The
// two declare no key in common, so the order is documentation, not a fix.
const textContainerSx = (theme: Theme) => ({
  ...v4GridItemSx,
  margin: "8px 0px",
  [theme.breakpoints.up("sm")]: {
    margin: "8px 0px",
  },
  [theme.breakpoints.up("md")]: {
    margin: "24px 0px",
  },
});

// was: useStyles().featureTitle
const featureTitleSx = (theme: Theme) => ({
  letterSpacing: "-1px",
  fontSize: "1.4rem",
  lineHeight: "1.4rem",
  fontWeight: 500,
  textShadow: "-1px 1px 1px #171717",
  marginBottom: "4px",
  [theme.breakpoints.up("sm")]: {
    fontSize: "1.6rem",
    lineHeight: "1.6rem",
  },
  [theme.breakpoints.up("md")]: {
    letterSpacing: "-1px",
    fontSize: "1.8rem",
    lineHeight: "1.8rem",
    fontWeight: 500,
    textShadow: "-2px 2px 2px #171717",
    marginBottom: "8px",
  },
});

// was: useStyles().featureDesc
const featureDescSx = (theme: Theme) => ({
  fontWeight: 500,
  fontSize: "1rem",
  lineHeight: "1.1rem",
  [theme.breakpoints.up("sm")]: {
    fontSize: "1.2rem",
    lineHeight: "1.3rem",
  },
});

const WhatIs = () => {
  return (
    <>
      <Background>
        <Container maxWidth="md" fixed>
          <Grid container sx={v4SpacingContainerSx}>
            <Grid size={{ xs: 12 }} sx={v4GridItemSx}>
              <Typography variant="h2" sx={titleSx}>
                What is
              </Typography>
              <Typography variant="h2" sx={titleLargeSx}>
                FarmFLiX?
              </Typography>
            </Grid>
            <Grid size={{ xs: 12, sm: 12, md: 4 }} sx={textContainerSx}>
              <Typography variant="h3" sx={featureTitleSx}>
                Hours of Agri Entertainment
              </Typography>
              <Typography variant="body1" sx={featureDescSx}>
                Access over 650 episodes of high quality content in Full HD.
              </Typography>
            </Grid>
            <Grid size={{ xs: 12, sm: 12, md: 4 }} sx={textContainerSx}>
              <Typography variant="h3" sx={featureTitleSx}>
                New Episode Added Weekly
              </Typography>
              <Typography variant="body1" sx={featureDescSx}>
                Huge variety of episodes across the AgLife, Stockyards and
                Straight6 channels.
              </Typography>
            </Grid>
            <Grid size={{ xs: 12, sm: 12, md: 4 }} sx={textContainerSx}>
              <Typography variant="h3" sx={featureTitleSx}>
                Stream and Enjoy on The App
              </Typography>
              <Typography variant="body1" sx={featureDescSx}>
                Access FarmFLiX on our new App. No limits. No ads.
              </Typography>
            </Grid>
          </Grid>
        </Container>
      </Background>
    </>
  );
};

export default WhatIs;
